import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  CheckCircle, Clock, CreditCard, Building2, FileText, Upload,
  Phone, Mail, MapPin, User, LogOut, ChevronRight, AlertCircle,
  Shield, Loader2, BadgeCheck, IndianRupee, Calendar, ArrowRight,
  Download, Eye, Trash2, FileCheck, XCircle, HelpCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTNER_TYPES = {
  TC: { name: "Training Center", fee: 50000 },
  DLC: { name: "District Partner", fee: 150000 },
  SP: { name: "State Partner", fee: 500000 }
};

const STATUS_CONFIG = {
  pending: { label: "Under Review", color: "amber", icon: Clock },
  stage1_approved: { label: "Stage 1 Approved", color: "blue", icon: CheckCircle },
  correction_required: { label: "Correction Required", color: "red", icon: AlertCircle },
  payment_pending: { label: "Payment Pending", color: "purple", icon: CreditCard },
  approved: { label: "Approved", color: "green", icon: BadgeCheck },
  active: { label: "Active", color: "emerald", icon: Building2 },
  rejected: { label: "Rejected", color: "red", icon: XCircle }
};

const PreAffiliationDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [centerUser, setCenterUser] = useState(null);
  const [application, setApplication] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('center_token');
    if (!token) {
      navigate('/center/login');
      return;
    }

    try {
      // Get center user info
      const userResponse = await axios.get(`${API}/center/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCenterUser(userResponse.data);

      // Get application tracking data
      if (userResponse.data.application_id) {
        const trackResponse = await axios.get(`${API}/partner/track/${userResponse.data.application_id}`);
        setApplication(trackResponse.data.application);
        setTimeline(trackResponse.data.timeline);
      }

      // Get uploaded documents
      const docsResponse = await axios.get(`${API}/center/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setDocuments(docsResponse.data || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('center_token');
        navigate('/center/login');
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('center_token');
    navigate('/center/login');
    toast.success('Logged out successfully');
  };

  const handlePayment = async () => {
    const token = localStorage.getItem('center_token');
    try {
      const response = await axios.post(`${API}/payment/create-partner-order`, {
        application_id: application.id,
        amount: application.fee
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Open Razorpay
      const options = {
        key: response.data.razorpay_key_id,
        amount: response.data.amount,
        currency: "INR",
        name: "Iqra Skills",
        description: `${PARTNER_TYPES[application.partner_type]?.name || 'Partner'} Fee`,
        order_id: response.data.order_id,
        handler: async function (razorpayResponse) {
          try {
            await axios.post(`${API}/payment/verify-partner-payment`, {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
              application_id: application.id
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Payment successful! Your center is being activated.');
            fetchDashboardData();
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: application.owner_name,
          email: application.email,
          contact: application.mobile
        },
        theme: { color: "#2563eb" }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);

    try {
      const token = localStorage.getItem('center_token');
      await axios.post(`${API}/center/upload-document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Document uploaded successfully');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to upload document');
    }
    setUploading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[application?.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold">Partner Dashboard</h1>
                <p className="text-slate-400 text-sm">Pre-Affiliation Portal</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{centerUser?.owner_name}</p>
                <p className="text-slate-400 text-sm">{centerUser?.email}</p>
              </div>
              <Button 
                onClick={handleLogout}
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-slate-700"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Status Banner */}
        <div className={`bg-${statusConfig.color}-500/10 border border-${statusConfig.color}-500/30 rounded-2xl p-6 mb-8`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 bg-${statusConfig.color}-500/20 rounded-xl flex items-center justify-center`}>
                <StatusIcon className={`w-7 h-7 text-${statusConfig.color}-400`} />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Application Status</p>
                <h2 className={`text-2xl font-bold text-${statusConfig.color}-400`}>
                  {statusConfig.label}
                </h2>
                {application?.temp_id && (
                  <p className="text-slate-500 text-sm font-mono mt-1">
                    ID: {application.temp_id}
                  </p>
                )}
              </div>
            </div>

            {application?.status === 'stage1_approved' && (
              <Button 
                onClick={handlePayment}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold px-6 py-3"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Pay ₹{(application.fee / 1000).toFixed(0)}K & Activate
              </Button>
            )}

            {application?.status === 'approved' && application?.center_code && (
              <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl px-6 py-3 text-center">
                <p className="text-emerald-400 text-sm">Center Code</p>
                <p className="text-2xl font-bold text-white font-mono">{application.center_code}</p>
              </div>
            )}
          </div>

          {application?.status === 'correction_required' && application?.correction_message && (
            <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <p className="text-red-400 font-medium flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Correction Required
              </p>
              <p className="text-red-300 mt-1">{application.correction_message}</p>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Timeline */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Application Timeline
              </h3>

              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700" />

                <div className="space-y-6">
                  {timeline.map((step, idx) => (
                    <div key={idx} className="relative flex items-start gap-4 pl-10">
                      {/* Timeline dot */}
                      <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        step.status === 'completed' ? 'bg-green-500' :
                        step.status === 'current' ? 'bg-blue-500 animate-pulse' :
                        'bg-slate-700'
                      }`}>
                        {step.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-white" />
                        ) : (
                          <span className="text-white text-sm font-bold">{step.step}</span>
                        )}
                      </div>

                      <div className="flex-1 pb-2">
                        <p className={`font-medium ${
                          step.status === 'completed' ? 'text-green-400' :
                          step.status === 'current' ? 'text-blue-400' :
                          'text-slate-500'
                        }`}>
                          {step.label}
                        </p>
                        {step.date && (
                          <p className="text-slate-500 text-sm">
                            {new Date(step.date).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-400" />
                Application Details
              </h3>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">Owner Name</p>
                      <p className="text-white font-medium">{application?.owner_name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">Mobile</p>
                      <p className="text-white font-medium">{application?.mobile}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">Email</p>
                      <p className="text-white font-medium">{application?.email}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">Center Name</p>
                      <p className="text-white font-medium">{application?.center_name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-pink-400" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">Location</p>
                      <p className="text-white font-medium">
                        {application?.city}, {application?.district}, {application?.state}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                      <IndianRupee className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm">Partner Type</p>
                      <p className="text-white font-medium">
                        {application?.partner_type_name} - ₹{(application?.fee / 1000).toFixed(0)}K
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Upload className="w-5 h-5 text-cyan-400" />
                Documents
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                {['Aadhaar Card', 'PAN Card', 'Address Proof', 'Center Photos'].map((docType) => {
                  const uploaded = documents.find(d => d.type === docType);
                  return (
                    <div 
                      key={docType}
                      className={`border-2 border-dashed rounded-xl p-4 ${
                        uploaded ? 'border-green-500/30 bg-green-500/5' : 'border-slate-600 hover:border-blue-500/50'
                      } transition-colors`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{docType}</span>
                        {uploaded ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <HelpCircle className="w-5 h-5 text-slate-500" />
                        )}
                      </div>
                      
                      {uploaded ? (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300">
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload(e, docType)}
                            disabled={uploading}
                          />
                          <span className="text-slate-400 text-sm hover:text-blue-400 flex items-center gap-1">
                            <Upload className="w-4 h-4" />
                            {uploading ? 'Uploading...' : 'Upload'}
                          </span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="text-slate-500 text-sm mt-4">
                * Documents are optional but help speed up the verification process
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payment Card */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-6 h-6" />
                <h3 className="font-bold text-lg">Payment Details</h3>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-blue-200">Partner Type</span>
                  <span className="font-medium">{application?.partner_type_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-200">Affiliation Fee</span>
                  <span className="font-bold text-xl">₹{application?.fee?.toLocaleString()}</span>
                </div>
                <hr className="border-white/20" />
                <div className="flex justify-between text-lg">
                  <span>Total Payable</span>
                  <span className="font-bold">₹{application?.fee?.toLocaleString()}</span>
                </div>
              </div>

              {application?.status === 'stage1_approved' && (
                <Button 
                  onClick={handlePayment}
                  className="w-full mt-4 bg-white text-blue-700 hover:bg-blue-50 font-bold"
                >
                  Pay Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}

              {application?.status === 'pending' && (
                <p className="text-blue-200 text-sm mt-4 text-center">
                  Payment will be enabled after Stage 1 approval
                </p>
              )}

              {application?.status === 'approved' && (
                <div className="mt-4 bg-green-500/20 rounded-lg p-3 text-center">
                  <CheckCircle className="w-5 h-5 inline mr-2" />
                  Payment Completed
                </div>
              )}
            </div>

            {/* Help Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
              <h3 className="font-bold text-white mb-4">Need Help?</h3>
              
              <div className="space-y-3">
                <a 
                  href="tel:+919521484003"
                  className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl hover:bg-slate-700 transition-colors"
                >
                  <Phone className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-white font-medium">Call Support</p>
                    <p className="text-slate-400 text-sm">+91 95214 84003</p>
                  </div>
                </a>

                <a 
                  href="https://wa.me/919521484003"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl hover:bg-slate-700 transition-colors"
                >
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">W</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">WhatsApp</p>
                    <p className="text-slate-400 text-sm">Quick Response</p>
                  </div>
                </a>

                <a 
                  href="mailto:support@iqraskills.org"
                  className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl hover:bg-slate-700 transition-colors"
                >
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <p className="text-slate-400 text-sm">support@iqraskills.org</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-amber-400" />
                Next Steps
              </h3>

              <div className="space-y-3 text-sm">
                {application?.status === 'pending' && (
                  <>
                    <p className="text-slate-300">1. Wait for Stage 1 approval (24-48 hours)</p>
                    <p className="text-slate-400">2. Complete payment after approval</p>
                    <p className="text-slate-400">3. Get your Center Code</p>
                    <p className="text-slate-400">4. Start training operations</p>
                  </>
                )}

                {application?.status === 'stage1_approved' && (
                  <>
                    <p className="text-green-400">✓ Stage 1 Approved</p>
                    <p className="text-slate-300">2. Complete payment to activate center</p>
                    <p className="text-slate-400">3. Get your Center Code</p>
                    <p className="text-slate-400">4. Start training operations</p>
                  </>
                )}

                {application?.status === 'approved' && (
                  <>
                    <p className="text-green-400">✓ Stage 1 Approved</p>
                    <p className="text-green-400">✓ Payment Completed</p>
                    <p className="text-green-400">✓ Center Code: {application.center_code}</p>
                    <p className="text-blue-400">→ Access full dashboard now!</p>
                  </>
                )}
              </div>

              {application?.status === 'approved' && (
                <Button 
                  onClick={() => navigate('/center/dashboard')}
                  className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  Go to Full Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PreAffiliationDashboard;
